import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import SrTable from 'client/components/SrTable';
import SrTableColumnFilter, {
  SR_TABLE_COLUMN_FILTER_TYPES,
} from 'client/components/SrTableColumnFilter';

const SrDataTableUI = (props) => {
  const { dataTableViewSettings, headers, autoScrollFunction, dataTableApiState, rows } = props;

  useEffect(() => {
    if (autoScrollFunction) {
      autoScrollFunction();
    }
  }, [props.rows, autoScrollFunction]);

  // Loading indicator is rendered inline so consumers don't have to bolt their own
  // spinner onto the page. We split the state into two cases so the UX matches
  // intent rather than blasting the same global spinner regardless of context:
  //   - First page (no rows yet): full-size loader where the table body would be.
  //     Same effective UX as the old per-consumer `<div className="loading-ellipses" />`.
  //   - Subsequent pages (rows already in state): keep the rows mounted and add a
  //     small "Loading more…" footer below the table. The grid stays usable while
  //     the next page is fetched instead of being implicitly hidden behind a global
  //     spinner that isn't visually tied to the table at all.
  // Opt-in: the loader only renders when the consumer actually passes
  // `dataTableApiState`, so existing callers keep their current behavior.
  const isLoading = !!dataTableApiState?.inProgress;
  const hasRows = (rows || []).length > 0;

  // Preserve the most recent non-empty `rows` so we can keep the table mounted
  // (with a dimmed overlay) while a filter/sort change is fetching the next batch.
  // Without this, the table briefly renders zero rows → its tbody unmounts → table
  // width collapses to whatever the header content needs → the browser clamps the
  // page's horizontal scroll back to 0. Kyle reported this as
  // "when a filter is applied on a column further right it snaps back to the left".
  // Showing the previous rows dimmed keeps the table the same width across the
  // transition, so the user's horizontal scroll position stays put.
  const previousRowsRef = useRef([]);
  useEffect(() => {
    if (hasRows) {
      previousRowsRef.current = rows;
    }
  }, [hasRows, rows]);

  // Reset the snapshot when `dataTableApiState` reports a settled non-loading
  // state with empty rows — that's the genuine "filter returned no matches" case
  // where we DO want to show the empty body so the user sees the empty result.
  useEffect(() => {
    if (!isLoading && !hasRows) {
      previousRowsRef.current = [];
    }
  }, [isLoading, hasRows]);

  const showStaleRows = !hasRows && isLoading && previousRowsRef.current.length > 0;
  const effectiveRows = showStaleRows ? previousRowsRef.current : rows;

  const _onHeaderClick = useCallback(
    (dataTableFieldId) => {
      if (dataTableViewSettings) {
        dataTableViewSettings.toggleSort(dataTableFieldId);
      }
    },
    [dataTableViewSettings],
  );

  const _onFilterChange = useCallback(
    (dataTableFieldId, nextValue) => {
      if (!dataTableViewSettings) return;
      const { update, filter, sort, fuzzy, customParams } = dataTableViewSettings;
      // Build a fresh filter map without the entry being changed, then add the new value
      // (or omit it entirely when nextValue is null/undefined). Note: `update` triggers a
      // viewId rehash, which forces a brand-new view object with loadedCount=0 in the
      // baobab store — pagination resets to page 1 automatically (per DW-466). We do not
      // need (and must not add) any explicit pagination-reset call here.
      const nextFilter = { ...(filter || {}) };
      delete nextFilter[dataTableFieldId];
      if (nextValue !== null && nextValue !== undefined) {
        nextFilter[dataTableFieldId] = nextValue;
      }
      update({ filter: nextFilter, sort, fuzzy, customParams });
    },
    [dataTableViewSettings],
  );

  const connectedHeaders = useMemo(
    () =>
      (headers || []).map((header) => {
        const filterValue =
          header.dataTableFieldId && dataTableViewSettings?.filter
            ? dataTableViewSettings.filter[header.dataTableFieldId]
            : undefined;
        const hasActiveFilter = filterValue !== undefined && filterValue !== null;

        const filterNode = header.filterConfig ? (
          <SrTableColumnFilter
            filterType={header.filterConfig.type}
            filterOptions={header.filterConfig.options}
            textPlaceholder={header.filterConfig.placeholder}
            dateInputMode={header.filterConfig.dateInputMode}
            ariaLabel={
              typeof header.content === 'string' ? `Filter ${header.content}` : 'Filter column'
            }
            value={filterValue}
            onChange={(nextValue) => _onFilterChange(header.dataTableFieldId, nextValue)}
          />
        ) : null;

        return {
          ...header,
          // Tag the header cell when a filter is active so the column reads as
          // "this is filtered" at a glance (in addition to the trigger badge).
          // Belt-and-suspenders for Kyle's "more obvious visual on which columns
          // are currently filtered" UAT note.
          className: [header.className, hasActiveFilter && 'hasActiveFilter']
            .filter(Boolean)
            .join(' '),
          sort: dataTableViewSettings?.sort?.[header.dataTableFieldId],
          filter: filterNode,
          onClick: !header.dataTableFieldId
            ? header.onClick
            : (...args) => {
                _onHeaderClick(header.dataTableFieldId);
                if (header.onClick) {
                  header.onClick(...args);
                }
              },
        };
      }),
    [headers, dataTableViewSettings, _onFilterChange, _onHeaderClick],
  );
  return (
    <>
      <div
        className="srDataTableUI-tableWrapper"
        // Wrap the table in a positioned container so the loading overlay
        // (rendered below) can absolutely-position itself over just the table
        // — not the whole dashboard. Inline style (no LESS file) keeps the
        // wrapper opt-in to consumers of SrDataTableUI without leaking global
        // CSS that other tables might inherit.
        style={{
          position: 'relative',
          opacity: showStaleRows ? 0.55 : 1,
          transition: 'opacity 120ms ease-out',
        }}
      >
        <SrTable {...props} rows={effectiveRows} headers={connectedHeaders} />
      </div>
      {isLoading && !hasRows && previousRowsRef.current.length === 0 && (
        <div className="loading-ellipses" />
      )}
      {isLoading && hasRows && (
        <div
          className="srDataTableUI-loadMoreFooter"
          style={{
            textAlign: 'center',
            padding: '12px 0',
            color: '#888',
            fontSize: '13px',
            fontStyle: 'italic',
          }}
        >
          Loading more…
        </div>
      )}
      {showStaleRows && (
        <div
          className="srDataTableUI-loadingOverlay"
          style={{
            textAlign: 'center',
            padding: '8px 0',
            color: '#888',
            fontSize: '13px',
            fontStyle: 'italic',
          }}
        >
          Updating…
        </div>
      )}
    </>
  );
};

SrDataTableUI.displayName = 'SrDataTableUI';

SrDataTableUI.propTypes = {
  ...SrTable.propTypes,
  dataTableViewSettings: PropTypes.object,
  // Optional: when provided, the table renders its own loading indicator
  // (full loader for first page, "Loading more…" footer for subsequent pages).
  // Shape comes from the `dataTable` HOC's withApiState binding.
  dataTableApiState: PropTypes.shape({
    inProgress: PropTypes.bool,
  }),
  // Headers accept everything SrTable accepts plus an optional `filterConfig` block that
  // declares the per-column filter UX. SrTable's own propTypes still validate the shared
  // header fields; we just add the new field here.
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node,
      onClick: PropTypes.func,
      className: PropTypes.string,
      style: PropTypes.object,
      sort: PropTypes.string,
      filterConfig: PropTypes.shape({
        type: PropTypes.oneOf(SR_TABLE_COLUMN_FILTER_TYPES).isRequired,
        options: PropTypes.array,
        placeholder: PropTypes.string,
        // Only meaningful when type === 'dateRange'. See the doc comment on
        // SR_TABLE_COLUMN_FILTER_DATE_INPUT_MODE_* in SrTableColumnFilter.js
        // for the per-column-type guidance.
        dateInputMode: PropTypes.string,
      }),
    }),
  ),
};

SrDataTableUI.displayName = 'SrDataTableUI';

export default SrDataTableUI;
